import type { SVGProps } from 'react';
import Chevron from '@assets/icons/chevron-down-filter.svg';
import CloseTagIcon from '@assets/icons/close-filled.svg';
import { BORDER_COLORS, DEFAULT_COLORS, SURFACE_COLORS, TEXT_COLORS } from '@constants/colors.ts';
import { DESKTOP_SIZES, FONT_FAMILY, SIZES } from '@constants/fonts.ts';
import { BODY_SM_SM_MEDIUM_STYLES, BODY_SM_SM_STYLES } from '@constants/text.ts';
import styled from 'styled-components';

import { BaseInput } from '../input';

export const ChevronToggleIcon = styled(Chevron)<{ $rotated: boolean } & SVGProps<SVGSVGElement>>`
    cursor: pointer;
    transition: transform 0.3s ease;
    transform: rotate(${(props) => (props.$rotated ? '180deg' : '360deg')});
`;

export const ChevronIconRight = styled(Chevron)`
    cursor: pointer;
    transform: rotate(270deg);
    transition: transform 0.2s ease;
`;

export const FilterWrapperLeft = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
`;

export const FilterSearchAction = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
`;

export const FilterSearchFilter = styled.div`
    display: flex;
    width: 480px;
    align-items: center;
    flex-shrink: 0;
`;

export const Search = styled.div<{ $active?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    flex-shrink: 0;
    ${({ $active }) =>
        $active &&
        `
        border-radius:  8px 0 0 8px;
        padding: 1px;
        border: 2px solid ${BORDER_COLORS.focus};
    `}
`;

export const DropDown = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    right: 0;
`;

export const DropDownInput = styled.div<{ $active?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;

    ${({ $active }) =>
        $active &&
        `
        border-radius: 0 8px 8px 0;
        padding: 1px;
        border: 2px solid ${BORDER_COLORS.focus};
    `}
`;

export const DropDownSearchField = styled.div`
    display: flex;
    padding: 10px 16px 11px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: 0 8px 8px 0;
    border: 1px solid ${BORDER_COLORS.default};
    background: ${DEFAULT_COLORS.white};
    ${FONT_FAMILY.poppins};
    color: ${TEXT_COLORS.body};
    ${DESKTOP_SIZES[SIZES.BODY_SM].fontSize};
    ${DESKTOP_SIZES[SIZES.BODY_SM].lineHeight};
    ${DESKTOP_SIZES[SIZES.BODY_SM].fontWeight};
`;

export const DropDownSearchFieldToggler = styled.div`
    display: flex;
    width: 16px;
    height: 16px;
    justify-content: center;
    align-items: center;
    svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
`;

interface DropdownProps {
    $show: boolean;
    $submenuRenderSide?: 'right' | 'left';
}

export const DropDownMenu = styled.div<DropdownProps>`
    position: absolute;
    top: 100%;
    left: ${({ $submenuRenderSide }) => ($submenuRenderSide === 'left' ? '-112px' : '0')};
    display: ${({ $show }) => ($show ? 'flex' : 'none')};
    flex-direction: ${({ $submenuRenderSide }) => ($submenuRenderSide === 'left' ? 'row-reverse' : 'row')};
    align-items: flex-start;
    gap: 4px;
    width: 100%;
    z-index: 10;
    margin-top: 4px;
`;

export const DropDownPlaceholder = styled.div`
    width: 100%;
    ${FONT_FAMILY.poppins};
    ${DESKTOP_SIZES[SIZES.BODY_SM].fontSize};
    ${DESKTOP_SIZES[SIZES.BODY_SM].lineHeight};
    ${DESKTOP_SIZES[SIZES.BODY_SM].fontWeight};
    color: ${TEXT_COLORS.body};
    user-select: none;
`;

export const DropDownMenuRollout = styled.div`
    display: flex;
    padding: 8px;
    align-items: flex-start;
    gap: 1px;
    border-radius: 4px;
    background: ${DEFAULT_COLORS.white};

    box-shadow:
        0 0 0 1px rgba(152, 161, 178, 0.1),
        0 15px 35px -5px rgba(17, 24, 38, 0.15),
        0 5px 15px 0 rgba(0, 0, 0, 0.08);
`;

export const DropDownMenuRightItems = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    flex: 1 0 0;
`;

export const DropDownMenuRightItemsInnerWrapper = styled.div`
    max-height: 270px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: ${BORDER_COLORS.focus} transparent;
`;

export const MenuRightItem = styled.div`
    cursor: pointer;
    display: flex;
    padding: 4px 10px;
    align-items: center;
    gap: 10px;
    align-self: stretch;
    border-radius: 4px;
    white-space: nowrap;
    background: ${DEFAULT_COLORS.white};

    &:hover {
        background: ${SURFACE_COLORS.page};
    }
`;

export const DropDownMenuLeftItems = styled.div`
    display: flex;
    cursor: pointer;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    flex: 1 0 0;
`;

export const MenuLeftItem = styled.div`
    display: flex;
    padding: 4px 10px;
    align-items: center;
    gap: 10px;
    align-self: stretch;
    border-radius: 4px;
    white-space: nowrap;
    background: ${DEFAULT_COLORS.white};
    svg {
        stroke: ${TEXT_COLORS.body};
    }
    &:hover {
        background: ${SURFACE_COLORS.action};

        svg {
            stroke: ${DEFAULT_COLORS.white};
        }
        * {
            color: ${DEFAULT_COLORS.white};
        }

        ${ChevronIconRight} path {
            stroke: ${DEFAULT_COLORS.white};
        }
    }
`;

export const DropDownSearchFieldIcon = styled.div`
    display: flex;
    width: 16px;
    height: 16px;
    justify-content: center;
    align-items: center;
    svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
`;

export const MenuLeftItemText = styled.div`
    flex: 1 0 0;
    ${FONT_FAMILY.poppins};
    color: ${TEXT_COLORS.body};
    ${DESKTOP_SIZES[SIZES.BODY_SM].fontSize};
    ${DESKTOP_SIZES[SIZES.BODY_SM].lineHeight};
    ${DESKTOP_SIZES[SIZES.BODY_SM].fontWeight};
`;

export const MenuLeftItemIco = styled.div`
    display: flex;
    width: 16px;
    height: 16px;
    justify-content: center;
    align-items: center;
`;

export const SearchFieldWrapper = styled(BaseInput)`
    && {
        border-radius: 8px 0 0 8px;
        color: ${TEXT_COLORS.filled};
        ${FONT_FAMILY.poppins};
        ${DESKTOP_SIZES[SIZES.BODY_SM].fontSize};
        ${DESKTOP_SIZES[SIZES.BODY_SM].lineHeight};
        ${DESKTOP_SIZES[SIZES.BODY_SM].fontWeight};

        input {
            &::placeholder {
                color: ${TEXT_COLORS.disabled};
            }
        }
        &:hover {
            border-color: ${BORDER_COLORS.default};
        }

        &:focus,
        &:focus-within,
        &.ant-input-focused {
            border-color: ${BORDER_COLORS.default};
        }
    }
`;

export const ClearAll = styled.div`
    cursor: pointer;
    margin-left: 16px;
    margin-top: -2px;
`;

export const FiltersList = styled.div`
    margin-bottom: 20px;
`;

export const TagParent = styled.span`
    color: ${SURFACE_COLORS.action};
    ${FONT_FAMILY.poppins};
    ${DESKTOP_SIZES[SIZES.BODY_XS_MED].fontSize}
    ${DESKTOP_SIZES[SIZES.BODY_XS_MED].fontWeight}
    ${DESKTOP_SIZES[SIZES.BODY_XS_MED].lineHeight}
`;

export const TagChild = styled.span`
    color: ${TEXT_COLORS.filled};
    ${FONT_FAMILY.poppins};
    ${DESKTOP_SIZES[SIZES.BODY_XS_MED].fontSize}
    ${DESKTOP_SIZES[SIZES.BODY_XS_MED].fontWeight}
    ${DESKTOP_SIZES[SIZES.BODY_XS_MED].lineHeight}
`;

export const FilterTitle = styled.span`
    color: ${TEXT_COLORS.filled};
    ${FONT_FAMILY.poppins};
    ${DESKTOP_SIZES[SIZES.BODY_SM_MED].fontSize}
    ${DESKTOP_SIZES[SIZES.BODY_SM_MED].fontWeight}
    ${DESKTOP_SIZES[SIZES.BODY_SM_MED].lineHeight}
`;

export const CloseIconWrapper = styled(CloseTagIcon)`
    display: inline-flex;
    vertical-align: middle;
    margin-bottom: 2px;
`;

export const FilterHeaderWrapper = styled.div`
    margin-top: 16px;
    margin-bottom: 8px;
    display: flex;
`;

export const SelectAllSwitchWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-radius: var(--border-radius-none, 0);
    border-bottom: 1px solid var(--colors-border-disabled, #d3dbe5);
    background: var(--colors-surface-primary, #fff);
    padding: var(--spacing-xs, 8px) 10px;
    margin-bottom: var(--spacing-xs, 8px);
    min-width: 164px;
`;

export const SelectAllSwitchTrigger = styled.div`
    cursor: pointer;
    color: var(--colors-text-action, #2171c6);
    ${BODY_SM_SM_MEDIUM_STYLES};
`;

export const SelectAllSwitchItemsCounter = styled.div`
    color: var(--colors-text-body, #50566e);
    ${BODY_SM_SM_STYLES};
`;
